import { Component, OnInit, OnDestroy } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { delay } from 'rxjs';
import { LoaderService } from './common/services/loader/loader.service';
import { FaviconService } from './shared/services/favicon.service';
import { LoginDetailsService } from './core/login/services/login-details.service';
import { IdleTimeoutInitializer } from './core/authentication/idle-timeout-initializer.service';
import { AuthService } from './core/authentication/auth.service';
import { RefreshTokenValidationService } from './core/authentication/refresh-token-validation.service';

@Component({
  selector: 'ds-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
})
export class AppComponent implements OnInit, OnDestroy {

  constructor(
    private spinner: NgxSpinnerService,
    private loader: LoaderService,
    private faviconService: FaviconService,
    private loginDetailsService: LoginDetailsService,
    private idleTimeoutInitializer: IdleTimeoutInitializer,
    private authService: AuthService,
    private refreshTokenValidationService: RefreshTokenValidationService
  ) { }

  ngOnInit() {
    localStorage.removeItem('EMAIL');
    this.listenToLoading();
    const favIcon = this.loginDetailsService.favIcon;
    setTimeout(() => {
      let title = this.loginDetailsService.title;
      this.faviconService.setTitle(title);
    }, 5000);
    this.faviconService.setFavicon(favIcon);

    // Start listening for page visibility changes (system wake, tab switch)
    if (this.authService.canNavigateToDashboard()) {
      console.log('[AppComponent] Starting refresh token validation service');
      this.refreshTokenValidationService.start();
    }

    // Check if session has expired due to page being closed
    this.validateSessionOnPageResume();

    // Initialize idle timeout system
    this.initializeIdleTimeout();
  }

  /**
   * Cleanup on component destroy
   */
  ngOnDestroy(): void {
    // Clean up refresh token validation service
    this.refreshTokenValidationService.stop();
  }

  /**
   * Validate session when user returns to the page
   * If too much time has passed since last activity, force logout
   */
  private validateSessionOnPageResume(): void {
    const lastActivityTime = sessionStorage.getItem('lastActivityTime');
    const idleTimeoutMinutes = 60; // Must match idleTimeoutConfig.idleTime
    const idleTimeoutMs = idleTimeoutMinutes * 60 * 1000;

    if (lastActivityTime && this.authService.canNavigateToDashboard()) {
      const now = Date.now();
      const timeSinceLastActivity = now - parseInt(lastActivityTime);

      console.log(`[AppComponent] Session validation: Last activity was ${Math.round(timeSinceLastActivity / 1000)}s ago`);

      if (timeSinceLastActivity > idleTimeoutMs) {
        console.warn('[AppComponent] Session expired due to inactivity - forcing logout');
        this.authService.forceLogout('session_expired_on_resume');
        return;
      }
    }

    // Update last activity time for future checks
    sessionStorage.setItem('lastActivityTime', Date.now().toString());
  }

  /**
   * Initialize idle timeout monitoring
   * This will start activity monitoring and token validation checks
   */
  private initializeIdleTimeout(): void {
    console.log('[AppComponent] Checking for idle timeout initialization...');
    
    // Check if user is authenticated before initializing
    if (this.authService.canNavigateToDashboard()) {
      console.log('[AppComponent] User authenticated - initializing idle timeout');
      setTimeout(() => {
        this.idleTimeoutInitializer.initialize();
      }, 1000); // Delay to ensure all services are ready
    } else {
      console.log('[AppComponent] User not authenticated - idle timeout not initialized');
    }
  }

  listenToLoading(): void {
    this.loader.loaderSubject
      .pipe(delay(0))
      .subscribe((loading) => {
        if (loading) {
          this.spinner.show();
        }
        else {
          this.spinner.hide();
        }
      });
  }
}
